import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { VaultCardFace } from '../components/CardFace'
import { EmptyState, SectionHeading, Skeleton, Stat } from '../components/ui'
import { useSellPositions, useVault } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import type { Position } from '../lib/api'
import { cents, dateOnly, percent, timeAgo } from '../lib/format'

type SortKey = 'newest' | 'value' | 'change'

export function VaultPage() {
  const { user, connectAndSignIn } = useAuth()
  const { data, isLoading } = useVault(Boolean(user))
  const sell = useSellPositions()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [sort, setSort] = useState<SortKey>('newest')

  const positions = useMemo(() => {
    const list = [...(data?.positions ?? [])]
    if (sort === 'value') list.sort((a, b) => b.valueCents - a.valueCents)
    if (sort === 'change') list.sort((a, b) => b.changeCents - a.changeCents)
    return list
  }, [data?.positions, sort])

  const selectedTotal = useMemo(
    () => positions.filter((p) => selected.has(p.id)).reduce((sum, p) => sum + p.valueCents, 0),
    [positions, selected],
  )

  if (!user) {
    return (
      <div className="py-10">
        <EmptyState
          title="Sign in to see your vault"
          body="Your cards live on your account, so the vault needs a signed-in wallet."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
              Connect wallet
            </button>
          }
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  const totals = data?.totals
  const change = (totals?.valueCents ?? 0) - (totals?.basisCents ?? 0)

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const doSell = async (ids: string[]) => {
    if (ids.length === 0) return
    await sell.mutateAsync(ids)
    setSelected(new Set())
  }

  return (
    <div className="py-6">
      <SectionHeading
        title="Vault"
        sub="Everything you have pulled and not yet sold, marked to the latest snapshot."
      />

      <div className="panel mb-5 flex flex-wrap items-end justify-between gap-6 p-5">
        <div className="flex flex-wrap gap-8">
          <Stat label="Positions" value={totals?.count ?? 0} />
          <Stat label="Cost basis" value={cents(totals?.basisCents ?? 0)} sub="Value when pulled" />
          <Stat
            label="Market value"
            value={cents(totals?.valueCents ?? 0)}
            sub="At last refresh"
          />
          <Stat
            label="Since pulled"
            value={`${change >= 0 ? '+' : '−'}${cents(Math.abs(change))}`}
            tone={change >= 0 ? 'up' : 'down'}
            sub={
              totals && totals.basisCents > 0
                ? percent(change / totals.basisCents, 2)
                : undefined
            }
          />
        </div>

        {positions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setSelected(
                  selected.size === positions.length
                    ? new Set()
                    : new Set(positions.map((p) => p.id)),
                )
              }
            >
              {selected.size === positions.length ? 'Clear selection' : 'Select all'}
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={selected.size === 0 || sell.isPending}
              onClick={() => void doSell([...selected])}
            >
              {sell.isPending
                ? 'Selling…'
                : selected.size === 0
                  ? 'Select cards to sell'
                  : `Sell ${selected.size} for ${cents(selectedTotal)}`}
            </button>
          </div>
        )}
      </div>

      {positions.length === 0 ? (
        <EmptyState
          title="Nothing in the vault"
          body="Cards land here automatically the moment a pack is opened. You never have to claim them."
          action={
            <Link to="/packs" className="btn btn-primary btn-sm">
              Open your first pack
            </Link>
          }
        />
      ) : (
        <div className="panel overflow-hidden">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {selected.size > 0 ? `${selected.size} selected` : `${positions.length} positions`}
            </span>
            <div className="flex gap-1">
              {(
                [
                  ['newest', 'Newest'],
                  ['value', 'Value'],
                  ['change', 'Change'],
                ] as Array<[SortKey, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSort(key)}
                  className="rounded-md px-2 py-1 text-xs font-medium transition-colors"
                  style={{
                    background: sort === key ? 'var(--color-surface-3)' : 'transparent',
                    color: sort === key ? 'var(--color-text)' : 'var(--color-faint)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4 p-5">
            {positions.map((position) => (
              <PositionCard
                key={position.id}
                position={position}
                selected={selected.has(position.id)}
                onToggle={() => toggle(position.id)}
                busy={sell.isPending}
              />
            ))}
          </div>
        </div>
      )}

      {/* The selection has to stay actionable from wherever you made it. The
          Sell control in the header is fine at the top of a short vault and
          useless four screens down a long one, so once something is selected
          the action follows the viewport. */}
      {selected.size > 0 && (
        <div
          role="status"
          className="panel fixed inset-x-4 bottom-4 z-40 mx-auto flex max-w-lg items-center justify-between gap-4 px-4 py-3 sm:inset-x-auto sm:right-4 sm:left-auto"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold">
              {selected.size} card{selected.size === 1 ? '' : 's'} selected
            </div>
            <div className="tnum text-xs" style={{ color: 'var(--color-faint)' }}>
              {cents(selectedTotal)} at the latest snapshot
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button className="btn btn-quiet btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={sell.isPending}
              onClick={() => void doSell([...selected])}
            >
              {sell.isPending ? 'Selling…' : `Sell for ${cents(selectedTotal)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * One held card.
 *
 * The card itself is the select target — a grid of cards wants to be clicked,
 * not aimed at a 16px checkbox — so the whole tile toggles and the selected
 * state is carried by a ring in the brand colour plus a checkmark, rather than
 * by a control the card has to make room for. It is still a real `button` with
 * `aria-pressed`, so the keyboard and a screen reader get the same affordance
 * the pointer does.
 *
 * Sell is deliberately *not* on the card. With the tile itself selectable, a
 * second click target inside it is a mis-click waiting to happen, and the
 * header already has a Sell action that reads the selection — including a
 * selection of one.
 */
function PositionCard({
  position,
  selected,
  onToggle,
  busy,
}: {
  position: Position
  selected: boolean
  onToggle: () => void
  busy: boolean
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      aria-pressed={selected}
      aria-label={`${position.symbol}, ${position.rarity}, worth ${cents(position.valueCents)}`}
      className="group relative block w-full text-left transition-transform duration-200 hover:-translate-y-1 disabled:cursor-not-allowed"
    >
      <div
        className="aspect-[3/4.1] w-full transition-shadow"
        style={{
          boxShadow: selected
            ? '0 0 0 2px var(--color-brand), 0 6px 20px rgb(0 0 0 / 0.5)'
            : undefined,
        }}
      >
        <VaultCardFace
          symbol={position.symbol}
          name={position.name}
          kind={position.kind}
          rarity={position.rarity}
          quantity={position.quantity}
          priceNow={position.priceNow}
          valueCents={position.valueCents}
          basisCents={position.basisCents}
          changeCents={position.changeCents}
        />
      </div>

      {selected && (
        <span
          className="absolute top-2 right-2 grid size-5 place-items-center rounded-full text-[0.6875rem] font-bold"
          style={{ background: 'var(--color-brand)', color: '#101010' }}
          aria-hidden
        >
          ✓
        </span>
      )}

      {/* Where it came from. Off the card face itself, which is the printed
          object and has no business carrying provenance. */}
      <div
        className="mt-1.5 flex items-baseline justify-between gap-2 text-[0.625rem]"
        style={{ color: 'var(--color-faint)' }}
      >
        <span className="truncate">{position.packName}</span>
        <span className="shrink-0" title={dateOnly(position.acquiredAt)}>
          {timeAgo(position.acquiredAt)}
        </span>
      </div>
    </button>
  )
}
